import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

export default function SuperAdminOrgs() {
  const [orgs, setOrgs] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", plan: "starter" });
  const [saving, setSaving] = useState(false);

  const load = (query = "") => api.get(`/super-admin/organisations?q=${encodeURIComponent(query)}`).then(({ data }) => setOrgs(data)).catch(() => setOrgs([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/super-admin/organisations", form); toast.success("Organisation created"); setForm({ name: "", plan: "starter" }); setOpen(false); load(q); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl">
      <PageHeader title="Organisations" subtitle="Manage all tenants on the platform."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-accent text-accent-foreground hover:opacity-90" data-testid="create-org-btn"><Plus className="h-4 w-4 mr-1.5" /> New organisation</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create organisation</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="create-org-form">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="create-org-name" /></div>
                <div><Label>Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                    <SelectTrigger className="mt-1.5 capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button type="submit" disabled={saving} className="rounded-md" data-testid="create-org-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Search organisations…" className="pl-9" data-testid="org-search" />
      </div>

      {!orgs ? <Skeleton className="h-64 rounded-xl" /> : orgs.length === 0 ? (
        <EmptyState icon={Building2} title="No organisations" description="Create the first organisation to get started." />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="orgs-list">
          {orgs.map((o) => (
            <Link key={o.id} to={`/super-admin/organisations/${o.id}`} data-testid={`org-row-${o.id}`} className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><Building2 className="h-4 w-4" /></span>
                <div><p className="font-medium text-foreground">{o.name}</p><p className="text-xs text-muted-foreground">{o.member_count} members · {o.event_count} events</p></div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground capitalize">{o.plan}</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${o.status === "suspended" ? "bg-destructive/10 text-destructive" : "bg-accent/10 text-accent"}`}>{o.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
